/**
 * Utility functions for handling Mindee API data
 */

/**
 * Safely extracts a value from a Mindee field
 * @param {Object} mindeeData - The Mindee data object
 * @param {string} fieldName - The field name to extract
 * @param {*} defaultValue - Default value if field is not found
 * @returns {*} The extracted value or default value
 */
export const extractMindeeValue = (mindeeData, fieldName, defaultValue = '') => {
  if (!mindeeData) return defaultValue;
  
  const field = mindeeData[fieldName];
  if (!field) return defaultValue;
  
  // Handle different Mindee field types
  if (typeof field === 'object' && field !== null) {
    // Most Mindee fields have a 'value' property
    if (field.value !== undefined) {
      return field.value;
    }
    
    // Some fields might have different structures
    if (field.values && Array.isArray(field.values) && field.values.length > 0) {
      return field.values[0].value;
    }
  }
  
  return defaultValue;
};

/**
 * Formats a Mindee date value to a readable format
 * @param {Object} mindeeData - The Mindee data object
 * @param {string} fieldName - The date field name
 * @param {string} format - The desired format (default: DD/MM/YYYY)
 * @returns {string} Formatted date string
 */
export const formatMindeeDate = (mindeeData, fieldName, format = 'DD/MM/YYYY') => {
  // Get date directly from the flattened response
  const dateValue = mindeeData[fieldName];
  if (!dateValue) return '';
  
  // Handle ISO date format from Mindee
  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return dateValue; // Return as is if not valid date
    
    // Format as DD/MM/YYYY
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateValue; // Return original value if parsing fails
  }
};

/**
 * Extracts line items from Mindee data
 * @param {Object} mindeeData - The Mindee data object
 * @returns {Array} Formatted line items array
 */
export const extractLineItems = (mindeeData) => {
  if (!mindeeData || !mindeeData.line_items || !Array.isArray(mindeeData.line_items)) {
    return [];
  }
  
  return mindeeData.line_items.map(item => {
    return {
      description: extractMindeeValue(item, 'description', ''),
      quantity: parseFloat(extractMindeeValue(item, 'quantity', 1)),
      unit_price: parseFloat(extractMindeeValue(item, 'unit_price', 0)),
      total_price: parseFloat(extractMindeeValue(item, 'total_amount', 0))
    };
  }).filter(item => item.description || item.total_price > 0);
};

/**
 * Transforms raw Mindee data into a format compatible with the form
 * @param {Object} mindeeData - The raw Mindee data
 * @returns {Object} Transformed data for the form
 */
export const transformMindeeData = (mindeeData) => {
  if (!mindeeData) return {};
  
  return {
    // Basic invoice information
    invoice_number: mindeeData.invoice_number || '',
    invoice_date: formatMindeeDate(mindeeData, 'invoice_date'),
    due_date: formatMindeeDate(mindeeData, 'due_date'),
    purchase_order_number: mindeeData.purchase_order_number || '',
    reference_numbers: mindeeData.reference_numbers || '',
    payment_date: formatMindeeDate(mindeeData, 'payment_date'),
    
    // Seller information
    seller_name: mindeeData.supplier_name || '',
    seller_address: mindeeData.supplier_address || '',
    seller_phone: mindeeData.supplier_phone || '',
    seller_email: mindeeData.supplier_email || '',
    seller_website: mindeeData.supplier_website || '',
    seller_vat: mindeeData.supplier_vat || '',
    seller_siret: mindeeData.supplier_siret || '',
    seller_payment_details: mindeeData.supplier_payment_details || '',
    
    // Buyer information
    buyer_name: mindeeData.customer_name || '',
    buyer_address: mindeeData.customer_address || '',
    buyer_id: mindeeData.customer_id || '',
    buyer_vat: mindeeData.customer_vat || '',
    buyer_siret: mindeeData.customer_siret || '',
    shipping_address: mindeeData.shipping_address || '',
    billing_address: mindeeData.billing_address || '',
    
    // Document information
    document_type: mindeeData.document_type || '',
    document_type_extended: mindeeData.document_type_extended || '',
    
    // Financial information
    total_ht: parseFloat(mindeeData.total_net || 0),
    total_tax: parseFloat(mindeeData.total_tax || 0),
    total_ttc: parseFloat(mindeeData.total_amount || 0),
    currency: mindeeData.currency || 'EUR',
    taxes_detail: mindeeData.taxes_detail || [],
    
    // Payment information
    payment_method: '',
    payment_info: mindeeData.supplier_payment_details || '',
    notes: '',
    
    // Metadata
    confidence_score: mindeeData.confidence ? 
      (mindeeData.confidence.invoice_number || mindeeData.confidence.total_amount || 0) : 0,
    processing_metadata: mindeeData.processing_metadata || {},
    
    // Line items
    items: Array.isArray(mindeeData.line_items) ? mindeeData.line_items.map(item => ({
      description: item.description || '',
      product_code: item.product_code || '',
      quantity: parseFloat(item.quantity || 1),
      unit_price: parseFloat(item.unit_price || 0),
      total_price: parseFloat(item.total_amount || 0),
      tax_amount: parseFloat(item.tax_amount || 0),
      tax_rate: parseFloat(item.tax_rate || 0),
      unit_of_measure: item.unit_of_measure || ''
    })) : []
  };
};

/**
 * Extract VAT number from supplier registration numbers
 */
function extractSupplierVat(mindeeData) {
  if (!mindeeData || !mindeeData.supplier_registration_numbers || !Array.isArray(mindeeData.supplier_registration_numbers)) {
    return '';
  }
  
  const vatEntry = mindeeData.supplier_registration_numbers.find(reg => {
    return reg.type && reg.type.value && reg.type.value.toLowerCase() === 'vat';
  });
  
  return vatEntry ? vatEntry.value : '';
}

/**
 * Extract SIRET number from supplier registration numbers
 */
function extractSupplierSiret(mindeeData) {
  if (!mindeeData || !mindeeData.supplier_registration_numbers || !Array.isArray(mindeeData.supplier_registration_numbers)) {
    return '';
  }
  
  const siretEntry = mindeeData.supplier_registration_numbers.find(reg => {
    return reg.type && reg.type.value && ['siret', 'siren'].includes(reg.type.value.toLowerCase());
  });
  
  return siretEntry ? siretEntry.value : '';
}

/**
 * Extract VAT number from customer registration numbers
 */
function extractCustomerVat(mindeeData) {
  if (!mindeeData || !mindeeData.customer_registration_numbers || !Array.isArray(mindeeData.customer_registration_numbers)) {
    return '';
  }
  
  const vatEntry = mindeeData.customer_registration_numbers.find(reg => {
    return reg.type && reg.type.value && reg.type.value.toLowerCase() === 'vat';
  });
  
  return vatEntry ? vatEntry.value : '';
}

/**
 * Extract SIRET number from customer registration numbers
 */
function extractCustomerSiret(mindeeData) {
  if (!mindeeData || !mindeeData.customer_registration_numbers || !Array.isArray(mindeeData.customer_registration_numbers)) {
    return '';
  }
  
  const siretEntry = mindeeData.customer_registration_numbers.find(reg => {
    return reg.type && reg.type.value && ['siret', 'siren'].includes(reg.type.value.toLowerCase());
  });
  
  return siretEntry ? siretEntry.value : '';
}

/**
 * Extract payment information from supplier payment details
 */
function extractPaymentInfo(mindeeData) {
  if (!mindeeData || !mindeeData.supplier_payment_details || !Array.isArray(mindeeData.supplier_payment_details)) {
    return '';
  }
  
  return mindeeData.supplier_payment_details.map(detail => {
    if (detail.account_number && detail.account_number.value) {
      return `${detail.type ? detail.type.value + ': ' : ''}${detail.account_number.value}`;
    }
    return '';
  }).filter(Boolean).join('\n');
}
